import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ChevronLeft,
    Star,
    LayoutGrid,
    Type,
    PenTool,
    Upload,
    Bold,
    Italic,
    Underline,
    List,
    ListOrdered,
    Heading1,
    Heading2,
    Superscript,
    FunctionSquare,
    AlertCircle,
    X,
    Compass,
    CheckCircle2,
    Trophy,
    Target,
    Lightbulb,
    ArrowRight,
    Flame,
    Award
} from "lucide-react";
import Markdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkMath from "remark-math";
import "katex/dist/katex.min.css";
import "../KaTeX/sm-exam-styles.scss";
import ReferenceTools from "../KaTeX/ReferenceTools";
import ScribblePad from "./ScribblePad";
import PhotoUpload from "./PhotoUpload";
import MathEditorMain from "./MathEditorMain";

const SmartExam: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<"text" | "draw" | "upload" | "tools">("text");
    const [isAddingEquation, setIsAddingEquation] = useState(false);
    const [tempEquation, setTempEquation] = useState("x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}");
    const [equations, setEquations] = useState<string[]>(["x^2", "x^2"]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);

    const handleAddEquation = () => {
        if (tempEquation.trim()) {
            setEquations([...equations, tempEquation]);
            setIsAddingEquation(false);
        }
    };

    const handleFinish = () => {
        setIsSubmitted(true);
        setTimeout(() => {
            setIsSubmitted(false);
            setShowFeedback(true);
        }, 1500);
    };

    const questionMarkdown = `
# Organic Synthesis & Titration

A chemist is tasked with determining the percentage by mass of ethanoic acid ($\\text{CH}_3\\text{COOH}$) in a commercial "Super-Strength" cleaning vinegar. The label states it contains $8.0\\% \\pm 0.5\\% $ ethanoic acid by mass.

**Procedure:**

1. $20.00 \\text{ mL}$ of vinegar was pipetted into a $250.0 \\text{ mL}$ volumetric flask and made up to the mark with distilled water.
2. $20.00 \\text{ mL}$ aliquots of this diluted vinegar were titrated against $0.1025 \\text{ mol L}^{-1}$ NaOH solution.
3. The average titre was found to be $20.45 \\text{ mL}$.
4. Density of the original vinegar is $1.05 \\text{ g mL}^{-1}$.
  `;

    return (
        <div className="ataris-container">
            {/* Success State Overlay */}
            {isSubmitted && (
                <div className="submission-overlay" style={{ position: "fixed", inset: 0, background: "rgba(255,255,255,0.9)", backdropFilter: "blur(10px)", zIndex: 1000, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ color: "#10b981", animation: "bounce 1s infinite" }}>
                        <CheckCircle2 size={80} />
                    </div>
                    <h2 style={{ fontSize: "32px", fontWeight: "800", marginTop: "24px", color: "#0f172a" }}>Analyzing Work...</h2>
                    <p style={{ color: "#64748b", marginTop: "8px" }}>AI is marking your answer based on official syllabi.</p>
                </div>
            )}

            {/* Header */}
            <header className="ataris-header">
                <div className="header-left">
                    <button className="exit-button" onClick={() => navigate(-1)}>
                        <ChevronLeft size={20} />
                        <span>Exit</span>
                    </button>
                    <div className="header-divider"></div>
                    <div className="syllabus-path">
                        <span>Chemistry</span>
                        <ArrowRight size={12} />
                        <span>Unit 3</span>
                        <ArrowRight size={12} />
                        <span className="current-topic">Titration Analysis</span>
                    </div>
                </div>

                <div className="header-center">
                    <div className="progress-stats">
                        <div className="stat">
                            <Flame size={14} color="#f97316" />
                            <span>3 Day Streak</span>
                        </div>
                        <div className="stat">
                            <Award size={14} color="#3b82f6" />
                            <span>850 XP</span>
                        </div>
                    </div>
                </div>

                <div className="header-right">
                    <div className="difficulty-tag easy">EASY</div>
                    <button className="icon-button star">
                        <Star size={20} />
                    </button>
                    <button className="icon-button questions">
                        <LayoutGrid size={20} />
                    </button>
                    <div className="user-profile-mini">JD</div>
                </div>
            </header>

            {/* Main Content */}
            <main className="ataris-main">
                {/* Left Pane - Question */}
                <div className="pane left-pane" style={{ flex: showFeedback ? "0 0 40%" : "1" }}>
                    <div className="pane-header">
                        <div className="tags-container">
                            <span className="tag concept">TITRATION</span>
                            <span className="tag concept">ETHANOIC ACID</span>
                            <span className="tag source">WACE 2023</span>
                        </div>
                        <button className="report-button">
                            <AlertCircle size={14} />
                            <span>REPORT</span>
                        </button>
                    </div>

                    <div className="pane-content">
                        <div className="question-text">
                            <div className="markdown-content">
                                <Markdown
                                    remarkPlugins={[remarkMath]}
                                    rehypePlugins={[rehypeKatex]}
                                >
                                    {questionMarkdown}
                                </Markdown>
                            </div>

                            <div className="question-part">
                                <div className="part-label">a)</div>
                                <div className="part-content">
                                    <div className="marks-display">4 MARKS</div>
                                    <p>Calculate the percentage by mass of ethanoic acid in the original cleaning vinegar. Show all your working steps.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Pane - Editor / AI Feedback */}
                <div className="pane right-pane" style={{ background: showFeedback ? "#ffffff" : "#f8fafc" }}>
                    {!showFeedback ? (
                        <div className="editor-container" style={{ padding: 0 }}>
                            <MathEditorMain
                                onSubmit={(content: string) => handleFinish()}
                                activeTab={activeTab}
                                onTabChange={(tab: string) => setActiveTab(tab as any)}
                            />
                        </div>
                    ) : (
                        <div className="feedback-container">
                            <div className="feedback-header">
                                <div className="score-badge">
                                    <span className="earned">3</span>
                                    <span className="total">/ 4 Marks</span>
                                </div>
                                <div className="feedback-meta">
                                    <h3>AI Feedback Generated</h3>
                                    <p>Your answer covers most points but missed the density conversion step.</p>
                                </div>
                            </div>

                            <div className="feedback-sections">
                                <div className="feedback-section correctness">
                                    <h4><CheckCircle2 size={16} color="#10b981" /> What you got right</h4>
                                    <ul>
                                        <li>
                                            <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                                {"Correct calculation of $n(\\text{NaOH})$ used in titration."}
                                            </Markdown>
                                        </li>
                                        <li>Properly applied the 1:1 stoichiometric ratio.</li>
                                        <li>Correctly accounted for the 250mL/20mL dilution factor.</li>
                                    </ul>
                                </div>

                                <div className="feedback-section improvement">
                                    <h4><Target size={16} color="#ef4444" /> Areas for improvement</h4>
                                    <div className="improvement-item">
                                        <p>
                                            <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                                {"You used the volume of vinegar directly without converting it to mass using the provided density ($1.05 \\text{ g mL}^{-1}$)"}
                                            </Markdown>
                                        </p>
                                        <div className="hint">
                                            <Lightbulb size={14} />
                                            <span>
                                                <Markdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                                                    {"Remember: Percentage by mass requires $(\\text{mass of solute} / \\text{total mass of solution}) \\times 100$"}
                                                </Markdown>
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="feedback-section mastery">
                                    <h4>Syllabus Mastery Progress</h4>
                                    <div className="mastery-item">
                                        <div className="mastery-info">
                                            <span>Titration Calculations</span>
                                            <span>85%</span>
                                        </div>
                                        <div className="progress-bar">
                                            <div className="progress-fill" style={{ width: "85%" }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="feedback-footer">
                                <button className="secondary-button" onClick={() => setShowFeedback(false)}>Edit Answer</button>
                                <button className="primary-button" onClick={() => setShowFeedback(false)}>Next Question <ArrowRight size={18} /></button>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default SmartExam;
